/* eslint-disable ember/no-controller-access-in-routes, ember/no-runloop, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { scheduleOnce } from '@ember/runloop';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { hash } from '@linc-technologies/linced-common/utils/promise';

export default class GroupRoute extends Route {
  // Services
  @service
  router;

  @service
  store;

  model(params) {
    return hash({
      group:
        this.store.peekRecord('group', params.group_id) ||
        this.store.findRecord('group', params.group_id),
      learners: this.store.query('person', {
        type: PersonType.LEARNER,
        groupId: params.group_id,
        includeFormer: true,
      }),
    }).then((data) => data.group);
  }

  afterModel(group) {
    // group-member fragments cache `person` as a peekRecord lookup keyed only
    // on `personId`, so any learner whose person record wasn't in the store
    // when the computed first ran keeps a stale `null`. Now that we've just
    // loaded every group member's person record, invalidate those caches and
    // the dependent learner-filter computeds on the group so the UI reflects
    // the full roster.
    let learners = group.get('learners');
    if (learners && learners.forEach) {
      learners.forEach((l) => l.notifyPropertyChange('person'));
    }
    group.notifyPropertyChange('filteredLearners');
  }

  setupController(controller, model) {
    let previousGroup = controller.selectedGroup;
    super.setupController(...arguments);
    controller.set('selectedGroup', model);
    // Counts are refreshed by reporting.group.index's routeDidChange hook,
    // which also covers returning from write/review/view and leaving a
    // student — cases this hook never sees.

    if (previousGroup && previousGroup.get('id') !== model.get('id')) {
      scheduleOnce('afterRender', this, 'restartGroupAnimation');
    }
  }

  restartGroupAnimation() {
    let el = document.querySelector('.reporting-group-content');
    if (!el) {
      return;
    }
    el.classList.remove('reporting-group-content');
    void el.offsetHeight;
    // force reflow so the browser registers the removal
    el.classList.add('reporting-group-content');
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      controller.set('selectedGroup', null);
      controller.set('templateDraftInfo', {});
      controller.set('sidepeekOpen', false);
      controller.set('publishSidepeekOpen', false);
    }
  }

  // Block in-app navigation while a publish-with-capture run is in flight —
  // leaving mid-run could publish some reports without their captured visuals.
  // Pairs with the runner's beforeunload guard (tab close) and progress modal.
  activate() {
    super.activate(...arguments);
    if (this.onRouteWillChange) {
      this.router.off('routeWillChange', this.onRouteWillChange);
    }
    this.onRouteWillChange = (transition) => {
      // transition.abort() below fires a second routeWillChange for a
      // synthetic "settling back" transition with isAborted already true.
      // Bail out here or we'd re-run this check and call abort() on it too,
      // recursing forever.
      if (transition.isAborted) {
        return;
      }
      if (this.controller && this.controller.get('isPublishing')) {
        transition.abort();
        // Only toast once per publish run so rapid repeated nav attempts
        // (sidebar, breadcrumb, back button) don't stack identical toasts.
        if (!this.controller.publishNavBlocked) {
          this.controller.publishNavBlocked = true;
          this.controller.toaster.error(
            'Please wait for publishing to finish before navigating away.',
          );
        }
      } else if (this.controller) {
        this.controller.publishNavBlocked = false;
      }
    };
    this.router.on('routeWillChange', this.onRouteWillChange);
  }

  deactivate() {
    super.deactivate(...arguments);
    if (this.onRouteWillChange) {
      this.router.off('routeWillChange', this.onRouteWillChange);
      this.onRouteWillChange = null;
    }
  }
}
